import api from "./axios";

/* Images are files on the server's disk and PATHS in its database. Every form
   that carries a picture does the same two steps: send the bytes to
   POST /admin/media/{folder} first, then save the record with the relative path the
   upload answered with ("banners/1725890000000-4821.webp"). The record routes
   themselves are JSON and never see a file.

   The folder must be one the catalogue actually points at — banners,
   branding, categories, subcategories, products, gallery, tickets — and the
   server refuses anything else rather than creating a directory nothing reads.

   The route sits under /admin because uploading is a back-office act: the
   same guard that protects every other admin write protects this one.

   Rejections carry a real status and { code, message }: 415 for a file that
   is not a JPEG/PNG/WebP/GIF (judged by its bytes, not its name), 413 past
   8 MB, 403 for a non-admin token. The message is fit to show as-is. */
export async function uploadImage(file, folder) {
  const form = new FormData();
  form.append("image", file, file.name);
  try {
    /* the request interceptor leaves FormData alone, so the browser sets the
       multipart boundary itself */
    const res = await api.post(`/admin/media/${folder}`, form);
    return res.data.path;
  } catch (e) {
    /* A message, not an error object: every form's catch here reads
       `err || "Something went wrong"` straight into state, the convention the
       redux thunks set with rejectWithValue, and an object there would crash
       the render. */
    throw uploadError(e, file);
  }
}

/* Several files, in the order they were picked, so the first tile stays the
   first tile. Uploads run one after another rather than all at once: a phone
   on a shop's Wi-Fi does better with one stream, and a failure names the file
   it stopped on instead of leaving four half-done. */
export async function uploadImages(files, folder) {
  const paths = [];
  for (const file of files) paths.push(await uploadImage(file, folder));
  return paths;
}

/* The message worth showing for a failed upload — the server's own where it
   gave one, otherwise the file's name so the person knows which one. */
export function uploadError(e, file) {
  const msg = e?.response?.data?.message;
  if (msg) return msg;
  return file?.name ? `“${file.name}” could not be uploaded` : "The image could not be uploaded";
}
